import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Loading, MessagePlugin } from 'tdesign-react';
import { fetchWechatAuthUrl, wechatRegister, type WechatLoginResult } from '@/services/portal';
import { setPortalToken } from '@/utils/auth';
import { usePortalAccount } from '@/contexts/PortalAccountContext';
import './Login.css';

/** 与登录页共享：state / flow 的 sessionStorage key */
const STATE_KEY = 'portal:wx_state';
const FLOW_KEY = 'portal:wx_flow';

const genState = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const cleanupQuery = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
};

interface RegisterBannerState {
  status: 'PENDING' | 'REJECTED';
  nickname: string | null;
}

/**
 * 门户注册页（微信开放平台 - 网站应用扫码注册）
 *
 * 流程（扫码注册 / 已注册则等同登录）：
 *  1) 「微信扫码注册」按钮 → 生成 state + 写入 flow=register → 调后端拿授权 URL → 跳转
 *  2) 用户扫码 + 手机确认 → 微信回跳到 WX_REDIRECT_URI（约定是 /portal/login）
 *     - 登录页检测到 flow=register，会把 query 原样 forward 到 /portal/register
 *  3) 本页检测到 ?code → 校验 state → POST /portal/wechat/register：
 *     - status=APPROVED       → 已注册并通过审批 → 写 token → 跳 /portal
 *     - status=PENDING        → 新注册 / 已注册未审批 → 展示「已提交，待审批」
 *     - status=REJECTED       → 已注册但被拒 → 展示「账号已被禁用」
 *     （注册接口不会返回 NOT_REGISTERED）
 */
const PortalRegister = () => {
  const navigate = useNavigate();
  const { setAccount } = usePortalAccount();

  const [redirecting, setRedirecting] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [banner, setBanner] = useState<RegisterBannerState | null>(null);

  const exchangedRef = useRef(false);

  const handleRegisterResult = useCallback(
    (res: WechatLoginResult): boolean => {
      if (res.status === 'APPROVED' && res.token && res.account) {
        setPortalToken(res.token);
        setAccount({
          id: res.account.id,
          name: res.account.name,
          nickname: res.account.nickname,
          organization: res.account.organization,
        });
        MessagePlugin.success('账号已启用，自动登录成功');
        navigate('/portal', { replace: true });
        return true;
      }
      // PENDING（新注册 / 已注册待审批）、REJECTED（已禁用）统一在页面上展示状态
      setBanner({
        status: res.status as 'PENDING' | 'REJECTED',
        nickname: res.account?.nickname ?? null,
      });
      return false;
    },
    [navigate, setAccount],
  );

  const exchange = useCallback(
    async (code: string, state: string | null) => {
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      const savedState = sessionStorage.getItem(STATE_KEY);
      sessionStorage.removeItem(STATE_KEY);
      // 注册流程消费完毕，清掉 flow 标记
      sessionStorage.removeItem(FLOW_KEY);
      if (savedState && state && savedState !== state) {
        cleanupQuery();
        MessagePlugin.error('注册态校验失败，请重试');
        return;
      }

      setExchanging(true);
      try {
        const res = await wechatRegister(code, state ?? undefined);
        cleanupQuery();
        handleRegisterResult(res);
      } catch {
        cleanupQuery();
      } finally {
        setExchanging(false);
      }
    },
    [handleRegisterResult],
  );

  // 本页既接收来自登录页 forward 的 ?code=...，也兼容直接刷新。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      void exchange(code, params.get('state'));
    }
  }, [exchange]);

  const handleWechatRegister = async () => {
    setBanner(null);
    setRedirecting(true);
    try {
      const state = genState();
      sessionStorage.setItem(STATE_KEY, state);
      sessionStorage.setItem(FLOW_KEY, 'register');
      const { url } = await fetchWechatAuthUrl(state);
      window.location.assign(url);
    } catch {
      setRedirecting(false);
    }
  };

  const goLogin = () => {
    navigate('/portal/login');
  };

  if (exchanging) {
    return (
      <div className="portal-login-page">
        <div className="portal-login-card">
          <Loading text="正在完成注册..." />
        </div>
      </div>
    );
  }

  return (
    <div className="portal-login-page">
      <div className="portal-login-card">
        <div className="portal-login-title">指令提交系统 · 账号注册</div>
        <div className="portal-login-sub">使用微信扫码完成账号注册</div>

        {banner && <RegisterBanner banner={banner} />}

        <div className="portal-login-form">
          <Button
            theme="primary"
            size="large"
            block
            loading={redirecting}
            onClick={handleWechatRegister}
          >
            {banner ? '重新扫码注册' : '微信扫码注册'}
          </Button>
          <Button theme="default" variant="outline" size="large" block onClick={goLogin}>
            已有账号？去登录
          </Button>
        </div>

        <div className="portal-login-tip">注册完成后需经管理员启用后方可使用。</div>
      </div>
    </div>
  );
};

/** 注册页状态横幅：PENDING（待启用）/ REJECTED（已禁用） */
const RegisterBanner = ({ banner }: { banner: RegisterBannerState }) => {
  const isPending = banner.status === 'PENDING';
  const title = isPending ? '注册已提交，待启用' : '账号已禁用';
  const desc = isPending
    ? '您的微信账号已成功提交，请联系管理员启用后再登录。'
    : '该微信已被禁用，请联系管理员了解详情。';

  return (
    <div className={`portal-login-audit portal-login-audit--${isPending ? 'pending' : 'rejected'}`}>
      <div className="portal-login-audit-title">{title}</div>
      {banner.nickname && (
        <div className="portal-login-audit-nickname">微信昵称：{banner.nickname}</div>
      )}
      <div className="portal-login-audit-desc">{desc}</div>
    </div>
  );
};

export default PortalRegister;
